import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageBody, Toast } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { ensurePilotContext } from '@wundo/client/pilot.ts';
import { PageTitle } from '@/components/PageTitle.tsx';
import { AddMeetingButton } from '@/components/agenda/AddMeetingButton.tsx';
import { AddMeetingDialog } from '@/components/agenda/AddMeetingDialog.tsx';
import { AgendaBody } from '@/components/agenda/AgendaBody.tsx';
import { AgendaToolbar } from '@/components/agenda/AgendaToolbar.tsx';
import { CalendarSourcePill } from '@/components/agenda/CalendarSourcePill.tsx';
import { useAgendaBuckets } from '@/components/agenda/useAgendaBuckets.ts';
import { useAgendaMeetings } from '@/components/agenda/useAgendaMeetings.ts';
import { useAgendaSelect } from '@/components/agenda/useAgendaSelect.ts';
import { useAgendaView } from '@/components/agenda/useAgendaView.ts';
import { useAgendaWeek } from '@/components/agenda/useAgendaWeek.ts';
import { usePlanRequest } from '@/components/agenda/usePlanRequest.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { currentUserOrNull } from '@/lib/pilotEnded.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { useConnectedCalendars } from '@/lib/useConnectedCalendars.ts';
import { m } from '@/paraglide/messages.js';

/* The agenda is the app's landing page until there is a home page to land on. */
export const Route = createFileRoute('/_app/')({
  /*
   * Except for a pilot member, whose landing page is the oefenbibliotheek. The
   * pilot is about the practice conversations, and an agenda is the one page
   * that says nothing at all to somebody who is here for those — on the first
   * visit after onboarding and every time they open the app after that.
   */
  beforeLoad: async ({ cause, context, search }) => {
    /*
     * Where somebody lands is decided when they arrive. Opening the agenda from
     * inside the app, or tidying the mail's request out of the address, is not
     * an arrival: the agenda is still where a pilot member plans a conversation.
     */
    if (cause !== 'enter' || !context.arriving) {
      return;
    }

    /* A demo organisation is there to practise, so its members land there too. */
    const user = await currentUserOrNull(context.queryClient);
    if (user?.demo) {
      throw redirect({ to: '/practice', replace: true });
    }

    /* Unless the weekly mail asked for this page's form, which is only here. */
    if (search.plan) {
      return;
    }

    const pilot = await ensurePilotContext(context.queryClient);

    if (pilot !== null) {
      throw redirect({ to: '/practice', replace: true });
    }
  },
  component: AgendaRoute,
  /*
   * `?plan=1` is the weekly mail's "Gesprek inplannen": the form for writing a
   * conversation down is a dialog on this page, not a page of its own. The
   * router rewrites the address from what comes out of here and reads it back,
   * so `true` has to be recognised as well, or the request drops out on load.
   */
  validateSearch: (search: Record<string, unknown>): AgendaSearch => ({
    plan:
      search.plan === true || search.plan === 1 || search.plan === '1' ? true : undefined,
  }),
});

interface AgendaSearch {
  /** Whether to open the add-conversation form as the page comes up. */
  plan?: boolean;
}

/* Reading the search is the route's job, so the page itself stays renderable. */
function AgendaRoute() {
  const { plan } = Route.useSearch();

  return <AgendaPage planOnMount={plan === true} />;
}

export interface AgendaPageProps {
  /** Open the add-conversation form on mount, for a reader who asked for it. */
  planOnMount?: boolean;
}

/* Where the drawer beside this page is standing, for the nudge it asks for. */
const NUDGE_PAGE = { page: 'agenda' } as const;

/**
 * The user's week. With a calendar the week is the calendar's; without one,
 * writing a meeting down is the only way anything shows up here.
 */
export function AgendaPage({ planOnMount = false }: AgendaPageProps) {
  /* The one thing worth recommending here to somebody new (DEV-72). */
  const nudge = useChatPageNudge(NUDGE_PAGE);
  /* This page's conversation starters (DEV-16). */
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_agenda_unprepared(), icon: 'prepare' },
      { text: m.chat_quick_agenda_attention(), icon: 'week' },
    ],
    nudge,
  });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const { connected, noCalendar } = useConnectedCalendars();
  const { view, chooseView } = useAgendaView(user);
  const { buckets, toggle: toggleBucket } = useAgendaBuckets();
  const week = useAgendaWeek(user);
  const agenda = useAgendaMeetings({ userId: user?.id, week, noCalendar });
  const { select, linkToSuggestedStep, toast } = useAgendaSelect(user?.id);
  const plan = usePlanRequest({
    asked: planOnMount,
    canPlan: noCalendar && user !== undefined,
  });

  /* One "Gesprek toevoegen" on the page: while the empty state is up, it carries the button. */
  const addButton =
    noCalendar && user && !agenda.mayBeEmpty ? (
      <AddMeetingButton userId={user.id} />
    ) : null;

  return (
    <>
      <PageTitle>{m.nav_agenda()}</PageTitle>
      <PageBody>
        <AgendaToolbar
          view={view}
          onViewChange={chooseView}
          buckets={buckets}
          onFilterSelect={toggleBucket}
          onToday={week.showThisWeek}
          action={addButton}
          source={
            connected && (
              <CalendarSourcePill
                microsoft={connected.microsoft}
                google={connected.google}
              />
            )
          }
        />

        <div className='mt-6'>
          <AgendaBody
            content={agenda.content}
            view={view}
            week={week}
            buckets={buckets}
            onSelect={select}
            onLink={linkToSuggestedStep}
          />
        </div>
      </PageBody>

      {/* The form the button opens, opened by the mail: the button is not always on screen. */}
      {plan.asked && user && (
        <AddMeetingDialog userId={user.id} open={plan.open} onClose={plan.close} />
      )}

      {/* Outside PageBody: the toast belongs to the window. Its undo is what makes one click safe. */}
      <Toast
        open={toast.message !== null}
        message={toast.message ?? ''}
        actionLabel={toast.undoable ? m.agenda_undo() : undefined}
        onAction={toast.undoable ? toast.undo : undefined}
      />
    </>
  );
}
